// "Destiny": one lantern alone on a dark lake, hold and wait → moonlight sweeps across the water → a second lantern → the trees in blossom → a slow glide, the glows merge, time stands still → her painting emerges. One continuous camera move, solo violin.
export const destiny = {
  slug: "destiny",
  duration: 57.5,
  // the violin: soft and aching through the wait, rising as the moon breaks through, fullest as the lanterns merge, easing into the emergence
  music: "destiny-violin-wait",
  music2: [8.2, "destiny-violin"],
  calmMusic: "destiny-violin",
  stages: [
    { id: "wait", at: 0, hint: "the aching wait" },
    { id: "hold", at: 7.4, hint: "hold and wait" },
    { id: "table", at: 8.2, hint: "finally" },
    { id: "moon", at: 13, hint: "the moon breaks through" },
    { id: "lantern", at: 18, hint: "another light" },
    { id: "blossom", at: 27, hint: "the trees in blossom" },
    { id: "boat", at: 37, hint: "tap the water" },
    { id: "still", at: 49, hint: "time stands still" },
  ],
  hold: { at: 8, need: 3, auto: 10 },
  touch: { kind: "ripple", from: 37, until: 50 },
  // painted lake: the aching dark, then the moon breaking through over blossoming shores; two painted lanterns whose glows meet
  layers: [
    { src: "/arcs/destiny/dark.webp", wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.9, water: 0.38, mist: 0.18, mistCol: "#3b4a78", show: [-1, 0, 9, 15.5] },
    // finally: it begins at an ordinary restaurant table for two (two cups, a candle), whose walls dissolve into the starry lake
    { src: "/arcs/destiny/table.webp", wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.9, glow: 0.1, show: [7.8, 9.6, 13, 17.5] },
    { src: "/arcs/destiny/moon.webp", wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.9, water: 0.4, mist: [[8, 0.32], [30, 0.12]], mistCol: "#c9d3ee", show: [12.5, 17.5, 99, 99] },
    // the finally: a sky of countless bright stars over the moonlit lake, just you and me
    { src: "/arcs/destiny/stars.webp", add: true, wrap: 3.48, th: 0.2, h: 4.6, y: 4.6, r: 4.85, shimmer: 0.6, glow: 0.35, opacity: 0.9, show: [13.5, 18.5, 99, 99] },
    // two paper lanterns: ours, then theirs appearing on the far shore; the glide brings them side by side and their glows merge
    { src: "/arcs/destiny/paper-lantern.webp", black: true, flicker: 0.15, glow: 0.5, sway: 0.02, th: -0.09, y: 0.62, w: 0.85, h: 0.85, r: 4.2, show: [-1, 0, 54, 57] },
    { src: "/arcs/destiny/paper-lantern.webp", black: true, flicker: 0.15, glow: 0.5, sway: 0.02, th: 0.55, y: 1.0, w: 0.6, h: 0.6, r: 4.6, drift: [37, 48, -0.32, -0.49], show: [17.5, 21, 54, 57] },
    { src: "/arcs/destiny/lantern.webp", add: true, glow: 0.6, opacity: 0.65, th: -0.01, y: 0.75, w: 1.8, h: 1.8, r: 4.3, show: [45, 49.5, 54, 57] },
  ],
  pulse: { from: 2.5, to: 8.2, every: 1.3, quicken: true },
  moments: [
    { text: "When will I see you again?", from: 1.5, to: 7.6 },
    { text: "I found the one.", from: 49.5, to: 56.5 },
  ],
  line: { text: "Whatever our souls are made of, his and mine are the same.", cite: "Emily Brontë, Wuthering Heights", from: 40, to: 70, pos: "low" },
  ambient: { scene: "pond", until: 99, then: [] },
  // one slow, eased camera move: a gentle drift while waiting, then a glide across the lake toward the other light
  look: { start: 0.12, lift: [8, 30], end: 0.05, yaw: [[0, 0.32], [8, 0.18], [18, -0.06], [27, 0.05], [37, 0], [57.5, 0]] },
  dolly: [[0, 0], [8, 0.15], [30, 0.35], [37, 0.45], [48, 1.0], [57.5, 1.1]],
  fov: [[0, 74], [27, 71], [48, 67], [57.5, 66]],
  calm: { duration: 12, fade: [4, 8.5], line: [5.5, 11.5], backdrop: "destiny", pos: "low" },
  speed: [[0, 1], [57.5, 1]],
  reveal: {
    at: 57,
    style: "circular",
    center: [0.5, 0.3],
    duration: 11,
    focus: [
      { rect: [0, 0.72, 1, 0.28], style: "horizontal" },
      { rect: [0.04, 0.3, 0.92, 0.42], style: "vertical" },
      { rect: [0.25, 0.08, 0.5, 0.24], style: "circular" },
    ],
  },
  frag: `
uniform sampler2D uMap;
uniform vec4 uCrop;
uniform vec4 uRip[6];
uniform float uT, uAspect, uTileH, uRadius, uCy, uSurface, uPulse, uStill;
varying vec3 vPos;
${"__NOISE__"}
float hash1(float n){ return fract(sin(n * 91.7) * 43758.5453); }
float hash2(vec2 p){ return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }
float glint(vec2 p, float s){ return exp(-abs(p.x) * 90.0 / s - abs(p.y) * 9.0 / s) + exp(-abs(p.y) * 90.0 / s - abs(p.x) * 9.0 / s); }
// a paper lantern floating on the water: a warm rounded body with a dark cap
vec3 lantern(vec3 col, vec2 p, float s, float k, float pulse){
  vec2 q = p / s;
  float body = 1.0 - smoothstep(0.9, 1.0, length(vec2(q.x / 0.12, (q.y - 0.15) / 0.16)));
  float cap = step(abs(q.x), 0.07) * step(abs(q.y - 0.33), 0.025);
  col = mix(col, mix(vec3(1.0, 0.62, 0.25), vec3(1.0, 0.9, 0.6), smoothstep(0.16, 0.0, length(q - vec2(0.0, 0.14)))), body * k);
  col = mix(col, vec3(0.25, 0.12, 0.08), cap * k);
  return col + vec3(1.0, 0.6, 0.3) * exp(-length(q - vec2(0.0, 0.15)) * 3.2) * k * (0.35 + 0.25 * pulse);
}
// the far-shore trees: tall pointed crowns of sculpted dabs that burst into glowing blossom
vec3 trees(vec3 col, vec2 P, float bloom){
  float w = 0.6;
  float cell = floor(P.x / w);
  for (int k = -1; k <= 1; k++){
    float c = cell + float(k);
    float h = hash1(c * 3.7);
    float cx = (c + 0.5 + 0.3 * (h - 0.5)) * w;
    float ht = 1.0 + 1.3 * hash1(c + 11.0);
    float yy = (P.y - 1.3) / ht;
    if (yy < 0.0 || yy > 1.0) continue;
    float hw = 0.45 * w * pow(1.0 - yy, 0.8) * (0.85 + 0.15 * sin(yy * 20.0 + h * 9.0));
    if (abs(P.x - cx) > hw) continue;
    vec2 q = vec2((P.x - cx) / 0.06, P.y / 0.04);
    float hh = hash2(floor(q) + c);
    float a = hh * 1.2 - 0.6;
    vec2 f = mat2(cos(a), -sin(a), sin(a), cos(a)) * (fract(q) - 0.5);
    float dab = 1.0 - smoothstep(0.3, 0.5, length(f * vec2(0.8, 1.6)));
    vec3 bl = hh < 0.3 ? vec3(0.92, 0.22, 0.25) : hh < 0.6 ? vec3(0.98, 0.55, 0.72) : hh < 0.8 ? vec3(0.45, 0.6, 0.98) : vec3(0.97, 0.96, 1.0);
    vec3 dark = vec3(0.05, 0.07, 0.12) * (0.8 + 0.4 * dab);
    float kb = bloom * smoothstep(0.0, 0.3, bloom - h * 0.6);
    col = mix(dark, bl * (0.7 + 0.4 * dab), kb * (0.4 + 0.6 * dab));
  }
  return col;
}
void main(){
  float n = snoise(vPos * 0.6) * 0.5 + 0.5;
  float th = atan(vPos.x, -vPos.z);
  float part = smoothstep(8.4, 17.0, uT);
  float bloom = smoothstep(27.0, 36.0, uT);
  float glide = smoothstep(37.0, 48.5, uT);
  float merge = smoothstep(45.0, 51.0, uT);
  // the moonlight sweeping across the water, from the far shore to your feet, carries each new light in
  float sweep = smoothstep(8.4, 15.5, uT);
  float flare = exp(-pow((uT - 19.0) * 0.9, 2.0)) + 0.6 * exp(-pow((uT - 28.0) * 0.8, 2.0));
  vec3 col;
  vec2 local;
  if (uSurface < 0.5) {
    float X = th * uRadius;
    vec2 P = vec2(X, vPos.y);
    // the night sky, the moon hidden until the clouds part
    col = mix(vec3(0.06, 0.08, 0.2), vec3(0.02, 0.03, 0.09), smoothstep(1.5, 7.0, P.y));
    vec2 sc = floor(P / 0.6);
    col += vec3(0.9, 0.92, 1.0) * glint(P - (sc + vec2(hash2(sc), hash2(sc + 1.7))) * 0.6, 0.5) * step(0.7, hash2(sc + 4.0)) * 0.4 * part;
    float md = length(P - vec2(0.0, 3.6));
    vec3 moon = mix(vec3(0.95, 0.96, 1.0), vec3(0.8, 0.84, 0.95), smoothstep(0.0, 0.9, md) * (0.6 + 0.4 * n));
    col += vec3(0.7, 0.78, 1.0) * exp(-md * 0.9) * (0.12 + 0.45 * part);
    col = mix(col, moon, (1.0 - smoothstep(0.86, 0.9, md)) * (0.15 + 0.85 * part));
    float cl = snoise(vec3((P.x - sign(P.x) * part * 4.0) * 0.35, P.y * 0.9, uT * 0.03)) * 0.5 + 0.5;
    float cloud = smoothstep(0.35, 0.65, cl) * smoothstep(2.0, 3.0, P.y) * (1.0 - part * smoothstep(3.5, 0.5, abs(P.x)));
    col = mix(col, mix(vec3(0.12, 0.13, 0.22), vec3(0.4, 0.42, 0.55), part), cloud * 0.92);
    col = trees(col, P, bloom);
    // the lake: dark water, the moon pouring silver, the blossoming trees mirrored
    if (P.y < 1.3) {
      vec2 R = vec2(P.x + 0.04 * sin(P.y * 30.0 + uT * 1.2), 2.6 - P.y);
      vec3 refl = trees(vec3(0.03, 0.05, 0.12), R, bloom) * 0.6;
      col = mix(vec3(0.02, 0.04, 0.1), refl, smoothstep(1.3, 0.9, P.y) * 0.0 + 0.8);
      float path = exp(-P.x * P.x * 1.5) * (0.5 + 0.5 * sin(P.y * 60.0 + snoise(vec3(P.x * 3.0, P.y * 20.0, uT * 0.5)) * 3.0));
      float reach = smoothstep(1.3 - sweep * 1.5, 1.45 - sweep * 1.5, P.y);
      col += vec3(0.75, 0.8, 0.95) * path * part * 0.55 * reach;
      col += vec3(0.8, 0.85, 1.0) * exp(-pow((P.y - (1.3 - sweep * 1.4)) * 9.0, 2.0)) * exp(-P.x * P.x * 0.25) * 0.35 * part * (1.0 - sweep * 0.7);
      vec2 gc = floor(P / vec2(0.35, 0.12));
      col += vec3(0.85, 0.9, 1.0) * glint(P - (gc + vec2(hash2(gc), 0.5)) * vec2(0.35, 0.12), 0.35) * step(0.8, hash2(gc + 2.0)) * (0.2 + 0.5 * part) * (0.5 + 0.5 * sin(uT * 3.0 + gc.x));
    }
    // one blossom falling in the long wait, then petals drifting onto the water
    float fy = 4.2 - min(uT, 8.0) * 0.42;
    vec2 fb = P - vec2(-0.9 + 0.25 * sin(uT * 0.9), fy);
    col = mix(col, vec3(0.98, 0.7, 0.8), (1.0 - smoothstep(0.85, 1.0, length(fb / vec2(0.06, 0.03)))) * (1.0 - smoothstep(7.5, 8.5, uT)));
    for (int i = 0; i < 12; i++){
      float fi = float(i);
      float ph = fract(uT * 0.06 + hash1(fi));
      vec2 c = vec2(-5.0 + hash1(fi + 2.0) * 10.0 + ph * 2.5, max(1.0 + 0.1 * hash1(fi + 8.0), 4.5 - ph * 5.0) + 0.2 * sin(uT + fi));
      float a = uT * (0.6 + hash1(fi + 5.0)) + fi;
      vec2 q = mat2(cos(a), -sin(a), sin(a), cos(a)) * (P - c);
      col = mix(col, mix(vec3(0.98, 0.7, 0.82), vec3(0.95, 0.3, 0.35), step(0.6, hash1(fi + 1.0))), (1.0 - smoothstep(0.85, 1.0, length(q / vec2(0.06, 0.03)))) * bloom * 0.9);
    }
    // our lantern, and theirs on the far shore, drawing close until the glows merge
    vec2 L1 = vec2(-0.35 + 0.05 * sin(uT * 0.4), 0.75 + 0.02 * sin(uT * 1.1));
    vec2 L2 = mix(vec2(2.4, 1.28), vec2(0.05, 0.78), glide) + vec2(0.0, 0.02 * sin(uT * 1.3));
    float s2 = mix(0.35, 1.0, glide);
    col = lantern(col, P - L1, 1.0, 1.0, uPulse);
    // a match-cut on the glow: their light answers ours as a flare, then settles
    col = lantern(col, P - L2, s2, smoothstep(17.5, 21.0, uT), flare);
    col += vec3(1.0, 0.7, 0.4) * exp(-length(P - L1) * 2.2) * flare * 0.25;
    vec2 mid = (L1 + L2) * 0.5 + vec2(0.0, 0.15);
    float still = smoothstep(50.0, 53.0, uT);
    col += vec3(1.0, 0.72, 0.45) * exp(-length((P - mid) * vec2(0.8, 1.2)) * 1.4) * merge * (0.45 + 0.2 * still + 0.05 * sin(uT * 0.7));
    local = vec2(X, P.y - uCy);
  } else if (uSurface < 1.5) {
    vec2 p = vPos.xz;
    // the lake at your feet, gliding past, moonlit where you tap
    vec2 flow = vec2(p.x, p.y + uT * 0.9 * glide * (1.0 - smoothstep(48.5, 51.0, uT)));
    float st = 0.5 + 0.5 * snoise(vec3(flow.x * 0.6, flow.y * 3.0, 1.0));
    col = mix(vec3(0.02, 0.04, 0.1), vec3(0.06, 0.09, 0.2), st) + vec3(0.6, 0.68, 0.9) * exp(-abs(p.x) * 1.4) * part * 0.12 * st;
    for (int i = 0; i < 6; i++){
      vec4 r = uRip[i];
      if (r.w <= 0.0) continue;
      float age = uT - r.z;
      float ring = abs(length(p - r.xy) - age * 1.1);
      col += vec3(0.8, 0.86, 1.0) * (1.0 - smoothstep(0.0, 0.06, ring)) * exp(-age * 0.8) * r.w * step(0.0, age) * 0.7;
    }
    col += vec3(1.0, 0.65, 0.35) * exp(-length(p - vec2(0.0, -uRadius + 0.8)) * 0.9) * 0.15;
    local = vPos.xz;
  } else {
    vec2 p = vPos.xz;
    col = vec3(0.03, 0.04, 0.12) + vec3(0.9, 0.92, 1.0) * glint(fract(p / 0.6) - 0.5, 0.5) * step(0.7, hash2(floor(p / 0.6))) * 0.4 * part;
    local = vPos.xz;
  }
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  #include <colorspace_fragment>
}`,
};
